import { useCallback, useState } from 'react'
import { MapPin, ShieldCheck, Zap, Settings, AlertTriangle } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/Dialog'
import { Button } from '../ui/Button'
import {
  HortonGeofence,
  isGeofenceSupported,
  probeNativePlugin,
} from '../../services/geofencePlugin'
import { BRAND } from '../../config/brand'

/**
 * Prominent-disclosure primer for location nudges. Shown BEFORE we ask Android
 * for location permissions, so the Play Store review team (and the user) see a
 * plain-English explanation of *why* yanamas needs background location.
 *
 * Flow:
 *  1. User reads the "what this does" screen.
 *  2. "Enable location nudges" -> requests FINE first.
 *  3. If granted, requests BACKGROUND. On Android 11+ the OS redirects this to
 *     a settings screen; we just reflect the new state when the user returns.
 *  4. We never retry automatically; if either prompt is denied, the user can
 *     try again from Settings or the /places screen.
 */
export interface LocationPermissionPrimerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onResult?: (result: { fine: boolean; background: boolean }) => void
}

type Step = 'intro' | 'requesting' | 'done' | 'unavailable'

type FailureReason =
  | 'denied_prompt_shown' // user saw the Android dialog and declined
  | 'denied_silent' // Android returned denied without showing a dialog (permanent deny)
  | 'plugin_missing' // APK is too old to include HortonGeofence

export function LocationPermissionPrimer({
  open,
  onOpenChange,
  onResult,
}: LocationPermissionPrimerProps) {
  const [step, setStep] = useState<Step>('intro')
  const [result, setResult] = useState<{ fine: boolean; background: boolean }>({
    fine: false,
    background: false,
  })
  const [failureReason, setFailureReason] = useState<FailureReason | null>(null)
  const [diagnosticMessage, setDiagnosticMessage] = useState<string | null>(null)

  const handleEnable = useCallback(async () => {
    setFailureReason(null)
    setDiagnosticMessage(null)
    if (!isGeofenceSupported()) {
      setStep('unavailable')
      setDiagnosticMessage(
        `Location nudges are only available in the ${BRAND.assistant} Android app.`,
      )
      onResult?.({ fine: false, background: false })
      return
    }

    // Probe the native side FIRST so we can distinguish an old APK (no plugin)
    // from an actual permission denial. This is the common cause of "no dialog
    // appeared" reports — the JS is calling a plugin that isn't in the build.
    const probe = await probeNativePlugin()
    if (!probe.available) {
      setStep('unavailable')
      setDiagnosticMessage(
        probe.reason === 'unimplemented'
          ? `This version of the app doesn't include the location-nudge module yet. Please reinstall or update the ${BRAND.name} app.`
          : `Couldn't reach the location service: ${probe.message ?? 'unknown error'}`,
      )
      onResult?.({ fine: false, background: false })
      return
    }

    // Capture the pre-request state. On Android, if FINE is already in a
    // permanent "denied" bucket (sdk 30+ "deny" or pre-30 "don't ask again"),
    // requestPermissionForAlias returns immediately without a dialog — that's
    // the "I tapped Enable but got no prompt" symptom. We use the before/after
    // comparison to detect this.
    const before = probe.diagnostics

    setStep('requesting')
    try {
      const r = await HortonGeofence.requestPermissions()
      const resolved = {
        fine: r.fine === 'granted',
        background: r.background === 'granted',
      }
      setResult(resolved)

      if (!resolved.fine) {
        // If the state before and after match and it's "denied", the OS didn't
        // show a dialog — the user either previously selected "Don't ask again"
        // or the alias mapping is off. Either way, the remedy is app Settings.
        const wasAlreadyDenied = before.fine === 'denied'
        setFailureReason(wasAlreadyDenied ? 'denied_silent' : 'denied_prompt_shown')
      } else if (!resolved.background) {
        // FINE granted but BACKGROUND denied — on Android 11+ this always
        // needs a trip to Settings; there is no in-app dialog for it.
        setFailureReason('denied_silent')
      }

      setStep('done')
      onResult?.(resolved)
    } catch (err) {
      setDiagnosticMessage(err instanceof Error ? err.message : String(err))
      setStep('unavailable')
      onResult?.({ fine: false, background: false })
    }
  }, [onResult])

  const handleOpenSettings = useCallback(async () => {
    try {
      await HortonGeofence.openAppSettings()
    } catch (err) {
      console.warn('[primer] openAppSettings failed', err)
    }
  }, [])

  const handleClose = useCallback(() => {
    setStep('intro')
    setFailureReason(null)
    setDiagnosticMessage(null)
    onOpenChange(false)
  }, [onOpenChange])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        Structure: header + scrollable body + sticky footer. The sticky footer
        keeps the primary CTA visible even on short Android screens where the
        body might overflow, and the Dialog's safe-area padding keeps the whole
        sheet above the gesture nav bar.
      */}
      <DialogContent className="max-w-md flex flex-col">
        <DialogHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand/15 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-brand" />
            </div>
            <DialogTitle>Location nudges</DialogTitle>
          </div>
          <DialogDescription className="mt-2">
            Tap a task like "buy groceries" once — {BRAND.assistant} quietly reminds you
            when you're actually at your usual grocery store.
          </DialogDescription>
        </DialogHeader>

        {step === 'intro' && (
          <>
            <div className="px-6 pb-4 space-y-3 overflow-y-auto">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-subtle">
                <ShieldCheck className="w-5 h-5 text-good mt-0.5 flex-shrink-0" />
                <div className="text-sm text-primary">
                  We only check places <strong>you</strong> saved to{' '}
                  <em>My Places</em>. {BRAND.assistant} never tracks where you go, never
                  sells your location, and never sends it to any third-party
                  service.
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-subtle">
                <Zap className="w-5 h-5 text-brand mt-0.5 flex-shrink-0" />
                <div className="text-sm text-primary">
                  Battery-safe: Android wakes the app only when you{' '}
                  <em>enter</em> one of your saved places (after a short dwell).
                  No continuous background GPS.
                </div>
              </div>
              <div className="text-xs text-secondary px-1">
                Android will ask for two permissions:{' '}
                <strong>Precise location</strong>, then{' '}
                <strong>Allow all the time</strong>. Both are required for the
                reminder to fire while your phone is in your pocket.
              </div>
            </div>

            <div className="px-6 py-4 border-t border-soft bg-surface flex flex-col-reverse sm:flex-row gap-2 sticky bottom-0">
              <Button
                variant="outline"
                onClick={handleClose}
                className="sm:flex-1"
              >
                Not now
              </Button>
              <Button onClick={handleEnable} className="sm:flex-1">
                Enable location nudges
              </Button>
            </div>
          </>
        )}

        {step === 'requesting' && (
          <div className="px-6 py-8 text-center text-sm text-secondary">
            Please respond to the system prompt…
          </div>
        )}

        {step === 'done' && (
          <>
            <div className="px-6 pb-4 space-y-3 overflow-y-auto">
              <PermissionRow label="Precise location" granted={result.fine} />
              <PermissionRow
                label="Allow all the time (background)"
                granted={result.background}
              />

              {/* Case A: FINE was silently denied — no dialog ever appeared */}
              {!result.fine && failureReason === 'denied_silent' && (
                <div className="p-3 rounded-lg bg-subtle text-sm text-primary">
                  <div className="font-medium mb-1">
                    Android didn't show the permission prompt.
                  </div>
                  <div className="text-secondary text-xs">
                    This usually means location was previously declined with
                    "Don't ask again". Open system settings and grant{' '}
                    <strong>Location → Allow</strong>, then come back here and
                    tap Enable again.
                  </div>
                </div>
              )}

              {/* Case B: FINE was denied after the dialog was shown */}
              {!result.fine && failureReason === 'denied_prompt_shown' && (
                <div className="text-xs text-secondary px-1">
                  You declined the location prompt. You can change this any
                  time in Android Settings and retry from <em>My Places</em>.
                </div>
              )}

              {/* Case C: FINE granted, BACKGROUND needs Settings (Android 11+) */}
              {result.fine && !result.background && (
                <div className="p-3 rounded-lg bg-subtle text-sm text-primary">
                  <div className="font-medium mb-1">
                    One more step: "Allow all the time"
                  </div>
                  <div className="text-secondary text-xs">
                    Android 11+ doesn't show a dialog for background location —
                    you have to enable it in system settings. Without it,
                    reminders only fire while the app is open.
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-soft bg-surface flex flex-col-reverse sm:flex-row gap-2 sticky bottom-0">
              {(failureReason === 'denied_silent' ||
                (result.fine && !result.background)) && (
                <Button
                  variant="outline"
                  onClick={handleOpenSettings}
                  className="sm:flex-1"
                >
                  <Settings className="w-4 h-4 mr-1.5" />
                  Open app settings
                </Button>
              )}
              <Button onClick={handleClose} className="sm:flex-1">
                Done
              </Button>
            </div>
          </>
        )}

        {step === 'unavailable' && (
          <>
            <div className="px-6 pb-4 space-y-3 overflow-y-auto">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-subtle">
                <AlertTriangle className="w-5 h-5 text-bad mt-0.5 flex-shrink-0" />
                <div className="text-sm text-primary">
                  {diagnosticMessage ??
                    "Location nudges aren't available right now."}
                </div>
              </div>
              <div className="text-xs text-secondary px-1">
                Tip: if you just installed an update, fully close and reopen the
                {BRAND.assistant} app so Android picks up the new native module.
              </div>
            </div>
            <div className="px-6 py-4 border-t border-soft bg-surface sticky bottom-0">
              <Button onClick={handleClose} className="w-full">
                Close
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function PermissionRow({ label, granted }: { label: string; granted: boolean }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-subtle text-sm">
      <span className="text-primary">{label}</span>
      <span className={granted ? 'text-good font-medium' : 'text-bad font-medium'}>
        {granted ? 'Granted' : 'Not granted'}
      </span>
    </div>
  )
}

export default LocationPermissionPrimer
